import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { pricingService } from '../services/pricing.service';
import { usePedidoStore } from '../stores/pedido.store';
import type { ProdutoPricing, ClientePricing, VendedorPricing } from '../services/pricing.service';
import type { PrecoCalculado } from '@azoupvendas/shared';

// AIDEV-NOTE: Hook que expoe calcularPreco() com acesso ao usuario autenticado
// e ao cliente/tabelaPreco do store do pedido. Simplifica o uso nos componentes.

export function usePricingService() {
  const { user } = useAuth();
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);
  const tabelaPrecoId = usePedidoStore((s) => s.tabelaPrecoId);

  const vendedor: VendedorPricing = useMemo(
    () => ({
      pode_selecionar_tabela: user?.pode_selecionar_tabela ?? false,
      tabela_preco_padrao: user?.tabela_preco_padrao ?? null,
      tipo_preco_base: (user?.tipo_preco_base ?? 'V') as 'V' | 'A',
      tem_regras_quantidade: user?.tem_regras_quantidade ?? false
    }),
    [
      user?.pode_selecionar_tabela,
      user?.tabela_preco_padrao,
      user?.tipo_preco_base,
      user?.tem_regras_quantidade
    ]
  );

  const clientePricing: ClientePricing = useMemo(
    () => ({
      CODIGO: clienteSelecionado?.CODIGO ?? 0,
      preco_varejo_atacado: clienteSelecionado?.preco_varejo_atacado
    }),
    [clienteSelecionado?.CODIGO, clienteSelecionado?.preco_varejo_atacado]
  );

  const calcularPreco = useCallback(
    (produto: ProdutoPricing, quantidade: number): PrecoCalculado => {
      return pricingService.calcularPreco(
        produto,
        clientePricing,
        vendedor,
        tabelaPrecoId,
        quantidade
      );
    },
    [clientePricing, vendedor, tabelaPrecoId]
  );

  const isPromocaoAtiva = useCallback((produto: ProdutoPricing): boolean => {
    return pricingService.isPromocaoAtiva(produto);
  }, []);

  return { calcularPreco, isPromocaoAtiva };
}
